import { useEffect, useState } from "react"
import { Form, Col, Modal, ModalBody, Button, Row } from "react-bootstrap"
import { AppPageWrapper } from "../../NavMenu/AppPageWrapper"
import ActionButton from "../../ActionButton"
import FromToDates from "../FromToDates"
import { useHistory } from "react-router"
import { Home } from "../../../config/Routes"
import DualListBox from "../../Administration/DualListBox"
import { AccountTypeID } from "../../../config/Constants"
import AutoFilterCompany from "../../../api/Companies/CompanyModel/AutoFilterCompany"
import { useSelector } from "react-redux"
import { isCompanyAdmin, isPortalAdmin } from "../../../authentication/isAdminRole"
import { AppState } from "../../../AppState"
import { GetSelectedAssignedClinic } from "../../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import { FaFileCsv, FaFilePdf, FaSync, FaSyncAlt } from "react-icons/fa"
import printPdf from "../../../utils/printPdf"
import FormatDate from "../../../formatters/FormatDate"
import SummaryReportGetPdf, { SummaryReportGetCsv, SummaryReportParams } from "../../../api/Reports/SummaryReportGet"
import FormatTimestampFileName from "../../../formatters/FormatTimestampFileName"
import exportCsv from "../../../utils/exportCsv"
import AutoFilterCompaniesGet from "../../../api/Companies/AutoFilterCompaniesGet"
import handleAPIError from "../../../utils/handleAPIError"
import { CompanyDataLoader } from "../../../api/Reports/CompanyDataLoader"
import IncludeArchivedOrders from "./IncludeArchivedOrders"

enum DateRange {
  CURRENT_MONTH = "Current Month",
  PREVIOUS_MONTH = "Previous Month",
  LAST_30_DAYS = "Last 30 Days",
  FROM_BEGINNING_OF_YEAR = "From beginning of year",
  CUSTOM_FILTER = "Custom"
}

enum DateType {
  EVENTED = "Event Date",
  REVIEWED = "Reviewed Date",
  ORDERED = "Order Date",
  TESTED = "Date Tested",
  SCHEDULED = "Schedule Date"
}

const DateTypeLabel = new Map<DateType, string>([
  [DateType.EVENTED, 'Collection Date'],
  [DateType.REVIEWED, DateType.REVIEWED],
  [DateType.ORDERED, DateType.ORDERED],
  [DateType.TESTED, DateType.TESTED],
  [DateType.SCHEDULED, DateType.SCHEDULED]
]);

const SummaryReport = () => {
  const portalAdmin = useSelector((state: AppState) => isPortalAdmin(state))
  const clinic = useSelector((state: AppState) => GetSelectedAssignedClinic(state))
  const history = useHistory();

  const [generatingCsv, setGeneratingCsv] = useState(false)
  const [generatingPdf, setGeneratingPdf] = useState(false)
  const [companies, setCompanies] = useState<CompanyDataLoader>({ data: [], loading: false })
  const [selectedCompanies, setSelectedCompanies] = useState<AutoFilterCompany[]>([])
  const [selectedDateType, setDateType] = useState<string>(DateType.ORDERED)
  const [selectedDateRange, setDateRange] = useState<string>(DateRange.CUSTOM_FILTER)
  const [from, setFrom] = useState<Date>()
  const [to, setTo] = useState<Date>()
  const [includeArchivedOrders, setIncludeArchivedOrders] = useState<boolean>(true)

  useEffect(() => {
    setCompanies(prev => ({ data: prev.data, loading: true }))
    const sub = AutoFilterCompaniesGet("").subscribe(
      res => setCompanies({ data: res, loading: false }),
      error => handleAPIError(error, () => setCompanies(prev => ({ ...prev, loading: false })))
    )
    return () => sub.unsubscribe()
  }, []);

  useEffect(() => {
    setSelectedCompanies(companies.data)
  }, [companies.data]);

  useEffect(() => {
    let from, to
    const toDay = new Date()
    const currentYear = toDay.getFullYear()
    const currentMonth = toDay.getMonth()
    switch (selectedDateRange) {
      case DateRange.CURRENT_MONTH:
        from = new Date(currentYear, currentMonth, 1)
        to = new Date(currentYear, currentMonth + 1, 0)
        break;
      case DateRange.PREVIOUS_MONTH:
        from = new Date(currentYear, currentMonth - 1, 1)
        to = new Date(currentYear, currentMonth, 0)
        break;
      case DateRange.FROM_BEGINNING_OF_YEAR:
        from = new Date(currentYear, 0, 1)
        to = new Date(currentYear, currentMonth, toDay.getDate())
        break;
      case DateRange.LAST_30_DAYS:
        from = new Date(toDay.setDate(toDay.getDate() - 30))
        to = new Date(currentYear, currentMonth, toDay.getDate())
        break;
      default:
        break;
    }
    setFrom(from)
    setTo(to)
  }, [selectedDateRange])

  const isParamsValid = () => (
    selectedCompanies.length > 0 &&
    !!selectedDateRange &&
    !!selectedDateType &&
    (selectedDateRange !== DateRange.CUSTOM_FILTER || (!!from && !!to))
  )

  const getReportParams = (): SummaryReportParams => ({
    AccountTypeID: portalAdmin ? 0 : AccountTypeID,
    Company: !portalAdmin || selectedCompanies.length != companies.data.length ? selectedCompanies?.map(a => a.Name) : [],
    DateRange: selectedDateRange,
    DateType: selectedDateType,
    DateFrom: FormatDate(from),
    DateTo: FormatDate(to),
    IncludeArchived: includeArchivedOrders
  })

  const handleCancelClick = () => history.push(Home)
  const handleClearClick = () => {
    setSelectedCompanies(companies.data)
    setDateRange(DateRange.CUSTOM_FILTER)
    setDateType(DateType.ORDERED)
    setFrom(undefined)
    setTo(undefined)
    setIncludeArchivedOrders(true)
  }

  const handleDateTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => setDateType(event.target.value)
  const handleDateRangeChange = (event: React.ChangeEvent<HTMLSelectElement>) => setDateRange(event.target.value)

  const handleGenerateCsvClick = () => {
    setGeneratingCsv(true)
    SummaryReportGetCsv(getReportParams()).subscribe(
      (results: string) => {
        setGeneratingCsv(false)
        exportCsv(results, FormatTimestampFileName("SummaryReport", "csv", new Date()))
      },
      error => handleAPIError(error, () => setGeneratingCsv(false))
    )
  }

  const handleGeneratePdfClick = () => {
    setGeneratingPdf(true)
    SummaryReportGetPdf(getReportParams()).subscribe(
      (results: string) => {
        setGeneratingPdf(false)
        printPdf(results)
      },
      error => handleAPIError(error, () => setGeneratingPdf(false))
    )
  }

  return (
    <AppPageWrapper parentClassName={"align-items-center"} showNav>
      <Modal.Dialog size="lg">
        <Modal.Header>
          <Modal.Title className="text-uppercase">Summary Report</Modal.Title>
        </Modal.Header>
        <ModalBody>
          <Form>
            {portalAdmin && <Form.Row>
              <Col>
                <Form.Label className="required">Companies</Form.Label>
                <DualListBox
                  disabled={companies.loading}
                  options={companies.data}
                  selected={selectedCompanies}
                  idKey="AccountID"
                  labelKey="Name"
                  availableTitle="Available"
                  selectedTitle="Selected"
                  onChange={setSelectedCompanies}
                />
              </Col>
            </Form.Row>}
            {!portalAdmin && <Form.Row className="form-group">
              <Form.Label column sm={2}>Company</Form.Label>
              <Col><Form.Control as="input" readOnly value={clinic?.Name} /></Col>
            </Form.Row>}
            <Form.Row className="form-group">
              <Form.Label column sm={2} className="required">Date Type</Form.Label>
              <Col>
                <Form.Control id="date-type" as="select" onChange={handleDateTypeChange} value={selectedDateType}>
                  {Array.from(DateTypeLabel).map(([k, v]) => <option value={k} key={k} selected={k == selectedDateType}>{v}</option>)}
                </Form.Control>
              </Col>
              <Col sm={1}></Col>
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column sm={2} className="required">Date Range</Form.Label>
              <Col>
                <Form.Control id="date-range" as="select" onChange={handleDateRangeChange} value={selectedDateRange}>
                  {Object.values(DateRange).map(r => <option value={r} key={r} selected={r === selectedDateRange}>{r}</option>)}
                </Form.Control>
              </Col>
              <Col sm={1}></Col>
            </Form.Row>
            {selectedDateRange === DateRange.CUSTOM_FILTER && <Form.Row>
              <Col sm={2}></Col>
              <Col>
                <FromToDates from={from} to={to} onChangeFrom={setFrom} onChangeTo={setTo} />
              </Col>
              <Col sm={1}></Col>
            </Form.Row>}
            <Form.Row>
              <Col>
                <IncludeArchivedOrders
                  value={includeArchivedOrders}
                  onChange={setIncludeArchivedOrders}
                />
              </Col>
            </Form.Row>
          </Form>
        </ModalBody>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelClick}>Cancel</Button>
          <Button variant="secondary" className="mr-auto" onClick={handleClearClick}>Clear Filter&nbsp;<FaSync /></Button>
          <ActionButton variant="secondary" disabled={companies.loading || !isParamsValid()} onClick={handleGeneratePdfClick} spin={generatingPdf} disableOnSpin>Export PDF&nbsp;<FaFilePdf /></ActionButton>
          <ActionButton variant="primary" disabled={companies.loading || !isParamsValid()} onClick={handleGenerateCsvClick} spin={generatingCsv} disableOnSpin>Export CSV&nbsp;<FaFileCsv /></ActionButton>
        </Modal.Footer>
      </Modal.Dialog>
    </AppPageWrapper >)
}
export default SummaryReport
